import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ProcurementModal = ({ part, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    part_id: part?.id,
    requested_quantity: part?.min_quantity || 10,
    urgency: 'normal',
    status: 'pending',
    preferred_supplier: part?.supplier || '',
    delivery_location: part?.location || '',
    requester_name: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const urgencyOptions = [
    { value: 'emergency', label: 'Emergency' },
    { value: 'urgent', label: 'Urgent' },
    { value: 'normal', label: 'Normal' },
    { value: 'low', label: 'Low Priority' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (formData?.requested_quantity <= 0) {
      alert('Quantity must be greater than 0');
      return;
    }

    if (!formData?.requester_name?.trim()) {
      alert('Requester name is required');
      return;
    }

    setIsSubmitting(true);
    await onSubmit?.(formData);
    setIsSubmitting(false);
  };

  const estimatedCost = parseFloat(part?.unit_cost || 0) * parseInt(formData?.requested_quantity || 0);

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Request Procurement</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {part?.part_name} ({part?.part_number})
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Current Stock Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-muted/30 border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground">Current Stock</p>
              <p className="text-2xl font-bold text-foreground">{part?.quantity}</p>
            </div>
            <div className="bg-muted/30 border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground">Minimum Stock</p>
              <p className="text-2xl font-bold text-foreground">{part?.min_quantity}</p>
            </div>
          </div>

          {/* Requested Quantity */}
          <Input
            type="number"
            label="Requested Quantity"
            required
            min="1"
            value={formData?.requested_quantity}
            onChange={(e) => handleChange('requested_quantity', parseInt(e?.target?.value) || 0)}
          />

          {/* Urgency */}
          <Select
            label="Urgency Level"
            required
            options={urgencyOptions}
            value={formData?.urgency}
            onChange={(value) => handleChange('urgency', value)}
          />

          {/* Preferred Supplier */}
          <Input
            label="Preferred Supplier"
            placeholder="Enter supplier name"
            value={formData?.preferred_supplier}
            onChange={(e) => handleChange('preferred_supplier', e?.target?.value)}
          />

          {/* Delivery Location */}
          <Input
            label="Delivery Location"
            placeholder="e.g., Warehouse A - Shelf 3"
            value={formData?.delivery_location}
            onChange={(e) => handleChange('delivery_location', e?.target?.value)}
          />

          {/* Requester Name */}
          <Input
            label="Requester Name"
            required
            placeholder="Enter your name"
            value={formData?.requester_name}
            onChange={(e) => handleChange('requester_name', e?.target?.value)}
          />

          {/* Notes */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Additional Notes
            </label>
            <textarea
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              placeholder="Add any special instructions or notes..."
              value={formData?.notes}
              onChange={(e) => handleChange('notes', e?.target?.value)}
              rows={3}
            />
          </div>

          {/* Estimated Cost */}
          <div className="bg-primary/10 border border-primary rounded-lg p-4">
            <p className="text-sm text-muted-foreground">Estimated Cost</p>
            <p className="text-2xl font-bold text-primary">${estimatedCost?.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Based on unit cost of ${parseFloat(part?.unit_cost || 0)?.toFixed(2)}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              <Icon name="ShoppingCart" size={18} className="mr-2" />
              Submit Request
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProcurementModal;
